<template>
  <div class="tag-input">
    <!-- Text Input -->
    <template v-if="definition.input_type === 'text'">
      <input
        type="text"
        :value="value"
        @input="handleInput($event.target.value)"
        :placeholder="definition.input_config.placeholder || ''"
        :maxlength="definition.input_config.max_length"
        class="form-control form-control-sm"
        :class="{'is-invalid': hasError}"
      />
    </template>

    <!-- Number Input -->
    <template v-else-if="definition.input_type === 'number'">
      <input
        type="number"
        :value="value"
        @input="handleInput($event.target.value)"
        :min="definition.input_config.min_value"
        :max="definition.input_config.max_value"
        :step="definition.input_config.step || 1"
        class="form-control form-control-sm"
        :class="{'is-invalid': hasError}"
      />
    </template>

    <!-- Select Input (single) -->
    <template v-else-if="definition.input_type === 'select'">
      <select
        :value="value"
        @change="handleInput($event.target.value)"
        class="form-control form-control-sm"
        :class="{'is-invalid': hasError}"
      >
        <option value="">Seleziona...</option>
        <option
          v-for="option in definition.input_config.options"
          :key="option.value"
          :value="option.value"
        >
          {{ option.label }}
        </option>
      </select>
    </template>

    <!-- Multiselect Input -->
    <template v-else-if="definition.input_type === 'multiselect'">
      <Multiselect
        v-model="localMultiselectValue"
        :options="definition.input_config.options"
        :multiple="true"
        :close-on-select="false"
        :clear-on-select="false"
        :preserve-search="true"
        label="label"
        track-by="value"
        :placeholder="$pgettext('tag_input.placeholder', 'Seleziona...')"
        :class="{'is-invalid': hasError}"
        @input="handleMultiselectChange"
      />
    </template>

    <!-- Checkbox Input -->
    <template v-else-if="definition.input_type === 'checkbox'">
      <div class="checkbox-group" style="margin-top: 10px">
        <div
          v-for="option in definition.input_config.options"
          :key="option.value"
          class="custom-control custom-checkbox custom-control-inline"
        >
          <input
            type="checkbox"
            :id="`${definition.code}_${option.value}`"
            :value="option.value"
            :checked="isChecked(option.value)"
            @change="handleCheckboxChange(option.value, $event.target.checked)"
            class="custom-control-input"
          />
          <label
            :for="`${definition.code}_${option.value}`"
            class="custom-control-label"
          >
            {{ option.label }}
          </label>
        </div>
      </div>
    </template>

    <!-- Date Input -->
    <template v-else-if="definition.input_type === 'date'">
      <input
        type="date"
        :value="value"
        @input="handleInput($event.target.value)"
        :min="definition.input_config.min_date"
        :max="definition.input_config.max_date"
        class="form-control form-control-sm"
        :class="{'is-invalid': hasError}"
      />
    </template>

    <!-- Tags Input -->
    <template v-else-if="definition.input_type === 'tags'">
      <PTagInput
        :value="tagsValue"
        @input="handleTagsInput"
        :allowed_tags="suggestedTags"
        :addable="true"
        :placeholder="$pgettext('tag_definitions.placeholder', 'Type and press Enter to add tags')"
      />
    </template>

    <!-- Error Message -->
    <div v-if="hasError" class="invalid-feedback d-block">
      {{ errorMessage }}
    </div>
  </div>
</template>

<script>
import PTagInput from '@/domain/core/components/PTagInput'
import Multiselect from 'vue-multiselect'

export default {
  name: 'TagInput',
  components: {
    PTagInput,
    Multiselect
  },
  props: {
    definition: {
      type: Object,
      required: true
    },
    value: {
      type: [String, Number, Array],
      default: ''
    },
    error: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      localMultiselectValue: []
    }
  },
  watch: {
    value: {
      immediate: true,
      handler(newVal) {
        if (this.definition.input_type === 'multiselect') {
          const options = this.definition.input_config?.options || []
          if (Array.isArray(newVal)) {
            this.localMultiselectValue = newVal.map(v => options.find(o => o.value === v)).filter(Boolean)
          } else {
            this.localMultiselectValue = []
          }
        }
      }
    }
  },
  computed: {
    hasError() {
      return this.error && this.error.length > 0
    },
    errorMessage() {
      return this.error && this.error.length > 0 ? this.error[0] : ''
    },
    suggestedTags() {
      // Extract suggestions for tags input type, sorted by count descending
      if (this.definition.input_type === 'tags' && this.definition.suggestions) {
        return [...this.definition.suggestions]
          .sort((a, b) => b.count - a.count)
          .map(s => s.value)
      }
      return []
    },
    tagsValue() {
      // Ensure value is always an array for PTagInput
      if (Array.isArray(this.value)) return this.value
      if (this.value) return [this.value]
      return []
    }
  },
  methods: {
    handleInput(value) {
      this.$emit('input', value)
    },
    handleMultiselectChange(selectedOptions) {
      // Convert array of option objects back to array of values
      const values = selectedOptions ? selectedOptions.map(opt => opt.value) : []
      this.$emit('input', values)
    },
    handleCheckboxChange(optionValue, checked) {
      let newValue = Array.isArray(this.value) ? [...this.value] : []

      if (checked) {
        if (!newValue.includes(optionValue)) {
          newValue.push(optionValue)
        }
      } else {
        newValue = newValue.filter(v => v !== optionValue)
      }

      this.$emit('input', newValue)
    },
    handleTagsInput(tags) {
      // PTagInput emits array of strings, pass it through
      this.$emit('input', tags)
    },
    isChecked(optionValue) {
      if (Array.isArray(this.value)) {
        return this.value.includes(optionValue)
      }
      return this.value === optionValue
    }
  }
}
</script>

<style scoped>

</style>
